import React, { useState, useEffect, useRef } from 'react';
import Login from './components/Login';
import SuperAdminPanel from './components/SuperAdminPanel';
import ProviderPanel from './components/ProviderPanel';
import ProviderRegistration from './components/ProviderRegistration';
import { API_BASE_URL } from './config';

function App() {
  const SUPER_ADMIN_EMAILS = ['lokeshchopra150@gmail.com', 'rajesh201rai@gmail.com'];
  // SSO Logic: Check URL for token and email if not in localStorage
  const queryParams = new URLSearchParams(window.location.search);
  const ssoToken = queryParams.get('token');
  const ssoEmail = queryParams.get('email');
  const ssoRole = queryParams.get('role');

  if (ssoToken && ssoEmail) {
    localStorage.setItem('authToken', ssoToken);
    localStorage.setItem('userEmail', ssoEmail);
    if (ssoRole) localStorage.setItem('adminRole', ssoRole);
    // Clean up URL
    window.history.replaceState({}, document.title, window.location.pathname);
  }

  const [authToken, setAuthToken] = useState(() => localStorage.getItem('authToken'));
  const [adminRole, setAdminRole] = useState(() => localStorage.getItem('adminRole'));
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const t = localStorage.getItem('authToken');
    const r = localStorage.getItem('adminRole');
    return !!(t && r);
  });
  const [isLoading, setIsLoading] = useState(() => {
    const t = localStorage.getItem('authToken');
    const r = localStorage.getItem('adminRole');
    return !!t && !r;
  });
  const [needsRegistration, setNeedsRegistration] = useState(false);
  const [registrationEmail, setRegistrationEmail] = useState('');
  // Lock panel from first paint: use role from localStorage so panel never flips until logout
  const [panelRole, setPanelRole] = useState(() => {
    const t = localStorage.getItem('authToken');
    const r = localStorage.getItem('adminRole');
    return t && r ? r : null;
  });
  const roleResolvedRef = useRef(false);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const role = localStorage.getItem('adminRole');

    if (!token) {
      setIsLoading(false);
      setIsAuthenticated(false);
      roleResolvedRef.current = false;
      return;
    }

    // If we have token but no role, fetch it once
    if (!role) {
      if (roleResolvedRef.current) return;
      roleResolvedRef.current = true;
      setIsLoading(true);
      fetch(`${API_BASE_URL}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => (res.ok ? res.json() : Promise.reject(res)))
        .then((data) => {
          const email = String(localStorage.getItem('userEmail') || '').trim().toLowerCase();
          const resolvedRole = SUPER_ADMIN_EMAILS.includes(email) ? 'super_admin' : (data.role || 'provider');

          setAdminRole(resolvedRole);
          localStorage.setItem('adminRole', resolvedRole);

          if (SUPER_ADMIN_EMAILS.includes(email)) {
            localStorage.setItem('adminStoreId', '');
          } else if (data.store_id != null) {
            localStorage.setItem('adminStoreId', data.store_id);
          }

          setIsAuthenticated(true);
        })
        .catch(() => {
          roleResolvedRef.current = false;
          handleLogout();
        })
        .finally(() => setIsLoading(false));
    } else {
      // We have role: only validate token in background; do not change role or re-fetch /admin/me
      // Only logout on 401/403 so a brief network error doesn't switch the user to login
      fetch(`${API_BASE_URL}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(res => {
          if (res.status === 401 || res.status === 403) handleLogout();
        })
        .catch(() => { /* ignore network errors; keep current panel */ });
    }
  }, []);

  // Lock which panel to show: set once when authenticated, clear only on logout
  useEffect(() => {
    if (isAuthenticated && !isLoading && adminRole && panelRole === null) {
      setPanelRole(adminRole);
    }
  }, [isAuthenticated, isLoading, adminRole, panelRole]);

  // Handle successful login
  const handleLoginSuccess = (token, isNewUser, needsReg, email) => {
    if (needsReg) {
      setNeedsRegistration(true);
      setRegistrationEmail(email);
      return;
    }

    setAuthToken(token);
    const role = localStorage.getItem('adminRole') || null;
    setAdminRole(role);
    setPanelRole(role);
    setIsAuthenticated(true);
  };

  const handleBackToLogin = () => {
    setNeedsRegistration(false);
    setRegistrationEmail('');
    setPanelRole(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('adminRole');
    localStorage.removeItem('adminStoreId');
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('isNewUser');
    localStorage.removeItem('adminRole');
    localStorage.removeItem('adminStoreId');
    setAuthToken(null);
    setAdminRole(null);
    setPanelRole(null);
    setIsAuthenticated(false);
    roleResolvedRef.current = false;
  };

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show provider registration if needed
  if (needsRegistration) {
    return <ProviderRegistration email={registrationEmail} onBackToLogin={handleBackToLogin} />;
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  // Show Super Admin or Provider panel based on locked panel role (never flips until logout)
  const roleForPanel = panelRole || adminRole;
  if (roleForPanel === 'super_admin') {
    return <SuperAdminPanel authToken={authToken} onLogout={handleLogout} />;
  }
  return <ProviderPanel authToken={authToken} onLogout={handleLogout} />;
}

export default App;